import React, { useEffect, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import crmClient from './crmClient';
import { formatCurrency, formatPhone, formatDateTime, daysSince } from './format';

const STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'open', label: 'Open' },
  { key: 'contacted', label: 'Contacted' },
  { key: 'won', label: 'Won' },
  { key: 'lost', label: 'Lost' },
  { key: 'ignored', label: 'Ignored' }
];

const STATUS_TONE = {
  open: '#f5a623',
  contacted: '#1d8cf8',
  won: '#2dce89',
  lost: '#ec250d',
  ignored: '#8a94a6'
};

const FEEDBACK_REASON_LABELS = {
  too_expensive: 'Too expensive',
  checking_rates: 'Just checking rates',
  went_another_way: 'Went another way',
  timing: 'Timing / not ready',
  other: 'Other'
};

const TEST_OUTREACH_EMAIL = 'warren@gophr.app';

const ACCESSORIAL_LABELS = {
  is_expedited: 'Expedited',
  is_super_expedited: 'Super expedited',
  is_roundtrip: 'Round trip',
  is_fork_lift: 'Forklift',
  is_dolly: 'Dolly',
  is_hazmat: 'Hazmat',
  is_pallet_jack: 'Pallet jack',
  is_twic_card: 'TWIC card',
  is_liftgate: 'Liftgate',
  is_hand_loading: 'Hand load/unload',
  is_weather_protection: 'Weather protection',
  is_medical: 'Medical',
  is_personal_protective_equipment: 'PPE',
  contains_alcohol: 'Alcohol',
  is_over_size: 'Oversize'
};

const addressText = loc => {
  if (!loc) return '\u2014';
  if (loc.address) return loc.address;
  const parts = [loc.address_1, loc.city, loc.state, loc.zip].filter(Boolean).join(', ');
  if (parts) return parts;
  if (loc.lat != null && loc.lng != null) return `${loc.lat}, ${loc.lng} (coordinates only)`;
  return '\u2014';
};

const AbandonedCartsView = () => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState('active');
  const [leads, setLeads] = useState([]);
  const [statusCounts, setStatusCounts] = useState({});
  const [loading, setLoading] = useState(false);
  const [rescanning, setRescanning] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailPreview, setDetailPreview] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    crmClient
      .listAbandonedQuotes({ status: statusFilter, page_size: 200 })
      .then(payload => {
        setLoading(false);
        setLeads((payload && payload.leads) || []);
        setStatusCounts((payload && payload.status_counts) || {});
      })
      .catch(() => {
        setLoading(false);
        setNotice({ type: 'error', text: 'Could not load abandoned carts.' });
      });
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const rescan = () => {
    setRescanning(true);
    setNotice(null);
    crmClient
      .detectAbandonedQuotes()
      .then(payload => {
        setRescanning(false);
        const s = (payload && payload.summary) || {};
        setNotice({ type: 'success', text: `Rescan complete — ${s.flagged || 0} new, ${s.updated || 0} refreshed, ${s.won || 0} won.` });
        load();
      })
      .catch(() => {
        setRescanning(false);
        setNotice({ type: 'error', text: 'Rescan failed.' });
      });
  };

  const updateStatus = (lead, status, notes) => {
    setBusyId(lead.crm_abandoned_quote_id);
    const body = typeof notes === 'string' ? { status, notes } : { status };
    crmClient
      .updateAbandonedQuote(lead.crm_abandoned_quote_id, body)
      .then(() => {
        setBusyId(null);
        setNotice({ type: 'success', text: `Marked ${status}.` });
        load();
      })
      .catch(() => {
        setBusyId(null);
        setNotice({ type: 'error', text: 'Update failed.' });
      });
  };

  // Mark won/lost with an optional reason note (captured inline).
  const markWithReason = (lead, status) => {
    const note = window.prompt(`Why was this cart ${status}? (optional note)`, lead.notes || '');
    if (note === null) return; // cancelled
    updateStatus(lead, status, note.trim());
  };

  const sendTestToMe = () => {
    const lead = leads[0];
    if (!lead) {
      setNotice({ type: 'error', text: 'No lead available to build a test from.' });
      return;
    }
    setBusyId('test');
    crmClient
      .sendAbandonedOutreach(lead.crm_abandoned_quote_id, { channel: 'email', test_to: TEST_OUTREACH_EMAIL })
      .then(() => {
        setBusyId(null);
        setNotice({ type: 'success', text: `Test win-back email sent to ${TEST_OUTREACH_EMAIL}.` });
      })
      .catch(err => {
        setBusyId(null);
        const message = err && err.response && err.response.data && err.response.data.message ? err.response.data.message : 'Could not send test email.';
        setNotice({ type: 'error', text: message });
      });
  };

  const sendOutreach = (lead, channel) => {
    if (channel === 'sms' && !lead.customer_phone) {
      setNotice({ type: 'error', text: 'No phone on file for this lead.' });
      return;
    }
    if (channel === 'email' && !lead.customer_email) {
      setNotice({ type: 'error', text: 'No email on file for this lead.' });
      return;
    }
    const who = lead.customer_name || lead.customer_email || 'this customer';
    if (!window.confirm(`Send a win-back ${channel.toUpperCase()} to ${who}?`)) {
      return;
    }
    setBusyId(lead.crm_abandoned_quote_id);
    crmClient
      .sendAbandonedOutreach(lead.crm_abandoned_quote_id, { channel })
      .then(() => {
        setBusyId(null);
        setNotice({ type: 'success', text: `Win-back ${channel} sent to ${who}.` });
        load();
      })
      .catch(err => {
        setBusyId(null);
        const message = err && err.response && err.response.data && err.response.data.message ? err.response.data.message : `Could not send ${channel}.`;
        setNotice({ type: 'error', text: message });
      });
  };

  const routeText = lead => [lead.pickup_summary, lead.dropoff_summary].filter(Boolean).join(' → ') || '—';

  const openDetails = lead => {
    setDetailLead(lead);
    setDetailData(null);
    setDetailLoading(true);
    setDetailPreview(null);
    crmClient
      .getAbandonedOutreachPreview(lead.crm_abandoned_quote_id)
      .then(payload => setDetailPreview((payload && payload.preview) || null))
      .catch(() => {});
    crmClient
      .getAbandonedQuoteDetail(lead.crm_abandoned_quote_id)
      .then(payload => {
        setDetailLoading(false);
        setDetailData((payload && payload.prefill) || null);
      })
      .catch(() => {
        setDetailLoading(false);
        setNotice({ type: 'error', text: 'Could not load quote details.' });
      });
  };

  const closeDetails = () => {
    setDetailLead(null);
    setDetailData(null);
    setDetailLoading(false);
    setDetailPreview(null);
  };

  const createOrderFromPrefill = () => {
    if (!detailData) return;
    history.push('/admin/delivery_creation', { abandonedPrefill: detailData });
  };

  return (
    <div className="crm-abandoned">
      <div className="crm-toolbar" style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <div className="crm-filter-tabs" style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {STATUS_FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              className={'crm-chip' + (statusFilter === f.key ? ' active' : '')}
              onClick={() => setStatusFilter(f.key)}
              style={{
                padding: '6px 12px',
                borderRadius: '16px',
                border: '1px solid rgba(255,255,255,0.15)',
                background: statusFilter === f.key ? '#1d8cf8' : 'transparent',
                color: statusFilter === f.key ? '#fff' : 'inherit',
                cursor: 'pointer'
              }}
            >
              {f.label}
              {f.key !== 'active' && statusCounts[f.key] != null ? ` (${statusCounts[f.key]})` : ''}
            </button>
          ))}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
          <button type="button" className="btn btn-simple btn-sm" onClick={sendTestToMe} disabled={busyId === 'test' || leads.length === 0} style={{ margin: 0 }} title={`Send a sample win-back email to ${TEST_OUTREACH_EMAIL}`}>
            {busyId === 'test' ? 'Sending…' : 'Send test to me'}
          </button>
          <button type="button" className="btn btn-primary btn-sm" onClick={rescan} disabled={rescanning} style={{ margin: 0 }}>
            {rescanning ? 'Rescanning…' : 'Rescan now'}
          </button>
        </div>
      </div>

      {notice ? (
        <div style={{ marginBottom: '12px', color: notice.type === 'error' ? '#ec250d' : '#2dce89', fontSize: '13px' }}>{notice.text}</div>
      ) : null}

      {loading ? (
        <p style={{ opacity: 0.7 }}>Loading…</p>
      ) : leads.length === 0 ? (
        <p style={{ opacity: 0.7 }}>No abandoned carts in this view.</p>
      ) : (
        <div className="table-responsive">
          <table className="table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Contact</th>
                <th>Quoted</th>
                <th>Route</th>
                <th>Quote age</th>
                <th>Status</th>
                <th>Feedback</th>
                <th style={{ textAlign: 'right' }}>Win back</th>
              </tr>
            </thead>
            <tbody>
              {leads.map(lead => {
                const busy = busyId === lead.crm_abandoned_quote_id;
                const age = daysSince(lead.latest_quote_at);
                const smsSent = Boolean(lead.sms_sent_at);
                const emailSent = Boolean(lead.email_sent_at);
                return (
                  <tr key={lead.crm_abandoned_quote_id}>
                    <td>
                      <strong>{lead.customer_name || 'Unknown'}</strong>
                      {lead.quote_count > 1 ? <div style={{ fontSize: '11px', color: '#8a94a6' }}>{lead.quote_count} quotes</div> : null}
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      {lead.customer_email || '—'}
                      {lead.customer_phone ? <div style={{ color: '#8a94a6' }}>{formatPhone(lead.customer_phone)}</div> : null}
                    </td>
                    <td>
                      <button
                        type="button"
                        onClick={() => openDetails(lead)}
                        title="Open quote details & create order"
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: '#1d8cf8', fontWeight: 700, textDecoration: 'underline', fontSize: 'inherit' }}
                      >
                        {lead.quoted_fee != null ? formatCurrency(lead.quoted_fee) : '—'}
                      </button>
                      {lead.vehicle_type ? <div style={{ fontSize: '11px', color: '#8a94a6' }}>{lead.vehicle_type.replace(/_/g, ' ')}</div> : null}
                    </td>
                    <td style={{ fontSize: '12px', maxWidth: '220px' }}>{routeText(lead)}</td>
                    <td style={{ fontSize: '12px' }}>{age != null ? `${age}d ago` : formatDateTime(lead.latest_quote_at)}</td>
                    <td>
                      <span style={{ color: STATUS_TONE[lead.status] || 'inherit', fontWeight: 600, textTransform: 'capitalize' }}>{lead.status}</span>
                      {lead.notes ? <div style={{ fontSize: '11px', color: '#8a94a6', maxWidth: '180px', whiteSpace: 'normal' }} title={lead.notes}>“{lead.notes}”</div> : null}
                    </td>
                    <td style={{ fontSize: '12px' }}>
                      {lead.feedback_reason ? (
                        <span style={{ color: '#fba540' }}>{FEEDBACK_REASON_LABELS[lead.feedback_reason] || lead.feedback_reason}</span>
                      ) : <span style={{ color: '#8a94a6' }}>—</span>}
                    </td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      <button type="button" className="btn btn-sm" disabled={busy} onClick={() => openDetails(lead)} style={{ margin: '2px', border: '1px solid #1d8cf8', background: 'transparent', color: '#1d8cf8', fontWeight: 600 }}>Details</button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={busy || !lead.customer_phone}
                        onClick={() => sendOutreach(lead, 'sms')}
                        title={smsSent ? `SMS sent ${formatDateTime(lead.sms_sent_at)}` : lead.customer_phone ? 'Send win-back SMS' : 'No phone on file'}
                        style={{ margin: '2px', fontWeight: 600, border: '1px solid ' + (smsSent ? '#2dce89' : '#1d8cf8'), background: smsSent ? '#2dce89' : 'transparent', color: smsSent ? '#fff' : '#1d8cf8', opacity: lead.customer_phone ? 1 : 0.45 }}
                      >
                        {smsSent ? 'SMS ✓' : 'SMS'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm"
                        disabled={busy || !lead.customer_email}
                        onClick={() => sendOutreach(lead, 'email')}
                        title={emailSent ? `Email sent ${formatDateTime(lead.email_sent_at)}` : lead.customer_email ? 'Send win-back email' : 'No email on file'}
                        style={{ margin: '2px', fontWeight: 600, border: '1px solid ' + (emailSent ? '#2dce89' : '#1d8cf8'), background: emailSent ? '#2dce89' : 'transparent', color: emailSent ? '#fff' : '#1d8cf8', opacity: lead.customer_email ? 1 : 0.45 }}
                      >
                        {emailSent ? 'Email ✓' : 'Email'}
                      </button>
                      {lead.status !== 'won' ? <button type="button" className="btn btn-sm" disabled={busy} onClick={() => markWithReason(lead, 'won')} style={{ margin: '2px', border: '1px solid #2dce89', background: 'transparent', color: '#2dce89', fontWeight: 600 }}>Won</button> : null}
                      {lead.status !== 'lost' ? <button type="button" className="btn btn-sm" disabled={busy} onClick={() => markWithReason(lead, 'lost')} style={{ margin: '2px', border: '1px solid #ec250d', background: 'transparent', color: '#ec250d', fontWeight: 600 }}>Lost</button> : null}
                      {lead.status !== 'ignored' ? <button type="button" className="btn btn-sm" disabled={busy} onClick={() => updateStatus(lead, 'ignored')} style={{ margin: '2px', border: '1px solid #8a94a6', background: 'transparent', color: '#aeb6c7', fontWeight: 600 }}>Ignore</button> : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <Modal isOpen={Boolean(detailLead)} toggle={closeDetails} size="lg" className="modal-dialog-centered">
        <ModalHeader toggle={closeDetails}>Abandoned quote details</ModalHeader>
        <ModalBody>
          {detailLoading ? (
            <p style={{ opacity: 0.7 }}>Loading quote…</p>
          ) : !detailData ? (
            <p style={{ opacity: 0.7 }}>No quote details available.</p>
          ) : (
            <div style={{ fontSize: '13px' }}>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '24px', marginBottom: '16px' }}>
                <div>
                  <div style={{ color: '#8a94a6', fontSize: '11px', textTransform: 'uppercase' }}>Customer</div>
                  <div style={{ fontWeight: 600 }}>{(detailData.customer && detailData.customer.name) || 'Unknown'}</div>
                  <div>{(detailData.customer && detailData.customer.email) || '—'}</div>
                  <div>{detailData.customer && detailData.customer.phone ? formatPhone(detailData.customer.phone) : '—'}</div>
                </div>
                <div>
                  <div style={{ color: '#8a94a6', fontSize: '11px', textTransform: 'uppercase' }}>Quote</div>
                  <div><strong>{detailData.quoted_fee != null ? formatCurrency(detailData.quoted_fee) : '—'}</strong>{detailData.vehicle_type ? ` · ${detailData.vehicle_type.replace(/_/g, ' ')}` : ''}</div>
                  <div>{detailData.distance != null ? `${Number(detailData.distance).toFixed(1)} mi` : ''}{detailData.weight ? ` · ${detailData.weight} lb` : ''}</div>
                  <div>{detailData.scheduled_for ? `Scheduled ${formatDateTime(detailData.scheduled_for)}` : 'On demand'}</div>
                </div>
              </div>

              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#8a94a6', fontSize: '11px', textTransform: 'uppercase' }}>Pickup</div>
                <div>{addressText(detailData.pickup)}</div>
                {detailData.pickup && detailData.pickup.lat != null ? (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${detailData.pickup.lat},${detailData.pickup.lng}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>Open map</a>
                ) : null}
              </div>
              <div style={{ marginBottom: '12px' }}>
                <div style={{ color: '#8a94a6', fontSize: '11px', textTransform: 'uppercase' }}>Dropoff</div>
                <div>{addressText(detailData.dropoff)}</div>
                {detailData.dropoff && detailData.dropoff.lat != null ? (
                  <a href={`https://www.google.com/maps/search/?api=1&query=${detailData.dropoff.lat},${detailData.dropoff.lng}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px' }}>Open map</a>
                ) : null}
              </div>

              {detailData.accessorials && Object.keys(detailData.accessorials).some(k => detailData.accessorials[k]) ? (
                <div style={{ marginBottom: '8px' }}>
                  <div style={{ color: '#8a94a6', fontSize: '11px', textTransform: 'uppercase' }}>Accessorials</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '4px' }}>
                    {Object.keys(detailData.accessorials)
                      .filter(k => detailData.accessorials[k] && ACCESSORIAL_LABELS[k])
                      .map(k => (
                        <span key={k} style={{ padding: '2px 8px', borderRadius: '10px', background: 'rgba(29,140,248,0.15)', fontSize: '12px' }}>{ACCESSORIAL_LABELS[k]}</span>
                      ))}
                  </div>
                </div>
              ) : null}

              <p style={{ color: '#8a94a6', fontSize: '12px', marginTop: '12px' }}>
                Addresses are reverse-geocoded from the quote’s GPS points — confirm them, pick the billing store, and add items on the create screen.
              </p>
            </div>
          )}
          {detailLead && detailLead.feedback_reason ? (
            <div style={{ marginTop: '12px', padding: '8px 10px', background: 'rgba(45,206,137,0.1)', border: '1px solid rgba(45,206,137,0.3)', borderRadius: '8px', fontSize: '13px' }}>
              <strong>Why they didn't book:</strong> {FEEDBACK_REASON_LABELS[detailLead.feedback_reason] || detailLead.feedback_reason}
              {detailLead.feedback_at ? <span style={{ color: '#8a94a6' }}> · {formatDateTime(detailLead.feedback_at)}</span> : null}
            </div>
          ) : null}
          {detailPreview ? (
            <div style={{ marginTop: '16px', borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '12px' }}>
              <div style={{ color: '#8a94a6', fontSize: '11px', textTransform: 'uppercase', marginBottom: '6px' }}>Win-back messages (what gets sent)</div>
              <div style={{ marginBottom: '10px' }}>
                <div style={{ fontWeight: 600, fontSize: '12px', marginBottom: '2px' }}>SMS</div>
                <div style={{ background: 'rgba(29,140,248,0.08)', border: '1px solid rgba(29,140,248,0.2)', borderRadius: '8px', padding: '8px 10px', whiteSpace: 'pre-wrap', fontSize: '13px' }}>{detailPreview.sms}</div>
              </div>
              {detailPreview.email ? (
                <div>
                  <div style={{ fontWeight: 600, fontSize: '12px', marginBottom: '2px' }}>Email</div>
                  <div style={{ fontSize: '12px', color: '#8a94a6', marginBottom: '2px' }}>Subject: {detailPreview.email.subject}</div>
                  <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 10px', whiteSpace: 'pre-wrap', fontSize: '13px' }}>{detailPreview.email.body}</div>
                </div>
              ) : null}
            </div>
          ) : null}
        </ModalBody>
        <ModalFooter>
          <button type="button" className="btn btn-simple btn-sm" onClick={closeDetails} style={{ margin: 0 }}>Close</button>
          <button type="button" className="btn btn-primary btn-sm" onClick={createOrderFromPrefill} disabled={!detailData} style={{ margin: 0 }}>Create order (prefilled)</button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default AbandonedCartsView;
